import type { CustomFieldGroupDefinition } from '__types__';
import type { FieldSpecBase } from './formKinds.types';

/**
 * The structure of an Atellio entity form: sections per value scope (Global,
 * then one per workspace), editable units in them, the fields of each unit.
 * Pure data, generic over the field specs (`S`) of the form's kinds; the
 * schema form engine renders it, an entity's resolver builds it.
 */

/** Any kind's spec, as far as the structure is concerned. */
export type AnyFieldSpecBase = FieldSpecBase<string>;

/** Where a custom-field value row lives. Maps to `custom_fields.enterprise_workspace_id` (null | number). */
export type ValueScope =
  | { type: 'global' }
  | { type: 'workspace'; workspaceId: number };

/** Where the user is browsing; team scope collapses to its workspace (CF lens rule). */
export type BrowsingScope =
  | { level: 'organization' }
  | { level: 'workspace'; workspaceId: number };

export type SelectOption = { value: string; label: string };

/**
 * Custom field groups are NOT a field kind. A group is a container for
 * ordinary member fields; the resolver turns a single group into a
 * FieldsUnit and an array group (`definition.is_array`) into a ListUnit.
 */
export type RowContainer =
  | { type: 'entity' }
  | { type: 'custom-field-group'; definition: CustomFieldGroupDefinition };

export const SECTION_READ_ONLY_REASONS = [
  'no-capability',
  'other-workspace',
  'archived-workspace',
  'display-only'
] as const;
export type SectionReadOnlyReason = (typeof SECTION_READ_ONLY_REASONS)[number];

export type SectionEditability =
  | { type: 'editable' }
  | { type: 'read-only'; reason: SectionReadOnlyReason };

export type UnitEditability =
  | { type: 'editable' }
  | { type: 'read-only'; reason: SectionReadOnlyReason | 'locked-by-type' };

/** Why a unit exists in a section. */
export type UnitOrigin =
  | { type: 'global' }
  | { type: 'overlay' }
  | { type: 'mirror'; globalUnitId: string };

/**
 * Inheritance state of a mirror unit: a workspace section shows the full
 * custom-field form; a field shows the Global value until it is overridden.
 */
export type UnitInheritance =
  | { type: 'not-applicable' }
  | { type: 'inherited' }
  | { type: 'overridden' };

export type UnitBase = {
  /** '<sectionId>/<field.key>'; list items get '<listId>/<rowId>' at runtime. */
  id: string;
  title: string;
  scope: ValueScope;
  origin: UnitOrigin;
  inheritance: UnitInheritance;
  editability: UnitEditability;
};

/** One independently saveable unit — one RHF instance in edit mode. */
export type FieldsUnit<S extends AnyFieldSpecBase> = UnitBase & {
  type: 'fields';
  container: RowContainer;
  fields: S[];
};

/** How new rows are added to a list: one button, or a menu of templates (e.g. custom rate / rate cards). */
/** One template of a list's "add from" picker, e.g. a rate card. */
export type ListAddTemplate = {
  id: string;
  label: string;
  /** Second line in the picker ("GBP 1,200 / day · +20% agency"); `null` for none. */
  description: string | null;
};

export type ListAddAction =
  /** One button adding a blank row. */
  | { type: 'single'; label: string }
  /**
   * A button adding a blank row, and beside it a picker adding a row from a
   * template ("Add rate" + "From rate card"). The blank row is the common
   * case, so it never hides behind a menu.
   */
  | {
      type: 'single-or-template';
      label: string;
      /** The blank row as the first item of a menu that also lists the templates (the header's “+”). */
      blank: { label: string; description: string };
      templates: { label: string; options: ListAddTemplate[] };
      /** Where the templates are kept, for one that isn't listed (rate cards settings); `null` when not allowed. */
      manage: { label: string; href: string } | null;
    };

/** A list of units: rates, array custom-field groups. Items are FieldsUnits instantiated per row. */
export type ListUnit<S extends AnyFieldSpecBase> = UnitBase & {
  type: 'list';
  container: RowContainer;
  item: { title: string; fields: S[] };
  add: ListAddAction;
  /** The entries keep an order of the person's own (stored); `false` when nothing stores one. */
  reorderable: boolean;
};

export type EditUnit<S extends AnyFieldSpecBase> = FieldsUnit<S> | ListUnit<S>;

export type ScopeSectionId = 'global' | `ws:${number}`;

/** Collapsible block in the pane: Global, then one per workspace. */
export type ScopeSection<S extends AnyFieldSpecBase> = {
  id: ScopeSectionId;
  title: string;
  scope: ValueScope;
  editability: SectionEditability;
  units: EditUnit<S>[];
  /** Workspace sections: units with inheritance `overridden` (header badge). */
  overriddenCount: number;
  defaultExpanded: boolean;
  /** Shown in place of units when the section has none. */
  emptyNote?: string;
};

export type EntitySchema<S extends AnyFieldSpecBase> = {
  /** What the form edits: `resource`, … */
  entity: string;
  mode: 'edit' | 'create';
  sections: ScopeSection<S>[];
};

export const valueScopeUtils = {
  toWorkspaceId(scope: ValueScope): number | null {
    return scope.type === 'global' ? null : scope.workspaceId;
  },
  fromWorkspaceId(workspaceId: number | null): ValueScope {
    return workspaceId === null
      ? { type: 'global' }
      : { type: 'workspace', workspaceId };
  },
  equals(a: ValueScope, b: ValueScope): boolean {
    return (
      valueScopeUtils.toWorkspaceId(a) === valueScopeUtils.toWorkspaceId(b)
    );
  },
  sectionId(scope: ValueScope): ScopeSectionId {
    return scope.type === 'global' ? 'global' : `ws:${scope.workspaceId}`;
  }
};
